import {
  approxWholeUnits,
  displayDecimals,
  HOUSE_CURRENCY_PREFIXES,
  isIsoCurrency,
  majorToMinor,
  minorToMajor,
  type FanEstimateRate,
} from "@libs/money";

/** The currency Lissen charges in: pounds. */
export const POUNDS = "GBP";

/** Whether an amount's currency is pounds, for words that only hold in
 *  pounds (a UK delivery note, `merchDeliveryNoteIn`) and for what a build
 *  from before other currencies can draw. Whether 1-tap buys a listing is
 *  never this: it is the journey's `demo`, the server's seller markets'
 *  verdict. */
export const inPounds = (currency: string) => currency === POUNDS;

/**
 * An amount in a store's own currency, as public pages, the purchase flow,
 * the app and the editors say it: "£45.00", "US$45.00", "CAD$45.00",
 * "AUS$45.00", "€45.00", and the currency's code before any other
 * ("NZD 45.00", "SEK 45.00"), with the currency's own decimals ("JPY 4,500",
 * "BHD 1.234"; money.ts). Every label that shows a fan a listing's or an
 * order's money says it this way, so a currency reads the same on a price,
 * a button, a fee and a total. `amount` is in major units (45 for 45.00).
 * `compact` leaves the decimals off a whole amount ("£20", "NZD 80", but
 * "£18.50"), for a surface that says prices short (the featured card) and
 * for the estimate's whole units.
 */
export function storeMoney(
  amount: number,
  currency: string,
  { compact = false }: { compact?: boolean } = {},
): string {
  const code = currency.toUpperCase();
  const digits = compact && Number.isInteger(amount) ? 0 : displayDecimals(code);
  if (code === POUNDS || code === "EUR")
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  const number = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
  const prefix = HOUSE_CURRENCY_PREFIXES[code];
  return prefix ? `${prefix}${number}` : `${code} ${number}`;
}

/** `storeMoney` for an amount in minor units of its currency (4500 for
 *  £45.00 and for ¥4,500). */
export const storeMoneyMinor = (minor: number, currency: string) =>
  storeMoney(minorToMajor(minor, currency), currency);

/** What a price says where its seller adds tax at checkout (a journey's
 *  `taxAdded`, LIS-1133): the price is before tax, and the fan confirms
 *  the exact total before Pay. */
export const LOCAL_TAX = "+ local tax";

/** `label`, with "+ local tax" after it where the seller adds tax at
 *  checkout (`taxAdded`), or the words that say its amount (`tax`,
 *  `localTaxWords`). */
export const withLocalTax = (label: string, taxAdded?: boolean | null, tax?: string | null) =>
  taxAdded ? `${label} ${tax ?? LOCAL_TAX}` : label;

/** What a total before tax says after it: "+ £0.20 local tax" where the
 *  tax is known (`orderTotal`'s `taxMinor`), else "+ local tax". */
export const localTaxWords = (taxMinor: number | null | undefined, currency: string) =>
  taxMinor == null ? LOCAL_TAX : `+ ${storeMoneyMinor(taxMinor, currency)} local tax`;

/**
 * `minor` minor units of `currency` (a listing's), in the fan's own
 * currency at the journey's `approx` rate, rounded to whole units
 * (`approxWholeUnits`, halves up), as `storeMoney` says it: €75.00 at
 * 0.84612 pounds a euro is "£63", ¥4,500 at 0.0052 is "£23", and in New
 * Zealand dollars "NZD 80". The amount behind "about £63" on a headline
 * price or a total (the listing design's §6); never on a fee, where whole
 * units help no one.
 */
export const formatApprox = (minor: number, currency: string, approx: FanEstimateRate) =>
  storeMoney(approxWholeUnits(minor, currency, approx), approx.currency, { compact: true });

/**
 * "about £63": `minor` minor units of `currency` (a listing's) as the fan
 * reads them in their own currency (`formatApprox`), said beside the
 * amount in the listing's, on Link in Bio and in the app alike. Only on a
 * journey's headline price and its total (the listing design's §6). Null
 * without an estimate (the journey's `approx` is absent where the fan's
 * currency is the listing's, a rate is missing or the rates are old), for
 * an estimate in the listing's own currency, and for a listing or fan
 * currency ISO 4217 does not list with a minor unit (a store's odd code,
 * "CNH"), which has no rate: never a failure where a price is shown.
 */
export function approxLabel(
  minor: number,
  currency: string,
  approx: FanEstimateRate | null | undefined,
): string | null {
  if (!approx || approx.currency.toUpperCase() === currency.toUpperCase()) return null;
  if (!isIsoCurrency(currency) || !isIsoCurrency(approx.currency.toUpperCase())) return null;
  return `about ${formatApprox(minor, currency, approx)}`;
}

/**
 * A 1-tap listing's headline price, as its page says it on Link in Bio and
 * in the app alike: "£15.00", or "From £15.00" when its tickets or sizes do
 * not all come to that (`from`), in its own currency (`storeMoney`); with
 * the estimate in the fan's own currency beside it where the journey has
 * one (`approx`, the listing design's §6): "From €25.00 (about £21)"; and
 * "+ local tax" after both where its seller adds tax at checkout
 * (`taxAdded`, LIS-1133): "US$45.00 (about £34) + local tax". `amount` is
 * in major units, as `vendorHeadlinePrice` and `merchHeadline` give it.
 */
export function headlinePriceLabel(
  price: { amount: number; currency: string; from?: boolean },
  approx?: FanEstimateRate | null,
  taxAdded?: boolean | null,
): string {
  const label = `${price.from ? "From " : ""}${storeMoney(price.amount, price.currency)}`;
  // Converted only where there is an estimate to show, in a currency ISO
  // lists: a store's odd code ("CNH") is shown as `storeMoney` shows it.
  const about =
    approx && isIsoCurrency(price.currency)
      ? approxLabel(majorToMinor(price.amount, price.currency), price.currency, approx)
      : null;
  return withLocalTax(about ? `${label} (${about})` : label, taxAdded);
}
